import { useCallback } from "react";
import {
  Box,
  Flex,
  Heading,
  Link,
  List,
  ListItem,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { DollarSign, Flag, LayoutGrid } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { getCurrentColor, headerLink } from "../../constants";
import MainPageFreeAmmo from "./MainPageFreeAmmo";
import useCookie from "../../hooks/useCookie";
import { data } from "../../data";
import { useAtom } from "jotai";
import { selectedCaliberAtom } from "../../atom/calibers";

const MainPageDiscoverSection = () => {
  const { colorMode } = useColorMode();
  const { cookies } = useCookie();
  const [, setSelectedCaliber] = useAtom(selectedCaliberAtom);

  const renderInfo = useCallback(
    (data) => {
      const { category, subCategories, categoryLink } = data;

      return (
        <>
          <Heading
            key={category}
            as="h3"
            fontSize="2xl"
            color={getCurrentColor(colorMode, "blackC.200", "whiteC.200")}
            fontFamily="mainFont"
            fontWeight="600"
            mb={2}
            textAlign="start"
          >
            {category}
          </Heading>

          <Flex
            gap={{ base: 0, sm: 2 }}
            flexDirection={{ base: "column", sm: "row" }}
          >
            <List
              display="flex"
              gap="5px"
              flexWrap="wrap"
              justifyContent="start"
              mb={{ base: "5px", md: "16px" }}
              flexDirection="column"
            >
              {subCategories.map(({ name, url }) => {
                return (
                  <ListItem key={name}>
                    <Link
                      as={RouterLink}
                      to={url}
                      state={headerLink(
                        // eslint-disable-next-line
                        category.toLowerCase().replace("/(?:s|-)?ammo/gi,", ""),
                        name,
                        cookies.zip,
                      )}
                      fontSize="16px"
                      fontFamily="mainFont"
                      fontWeight="500"
                      color={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "whiteC.200",
                      )}
                      borderBottom="1px solid"
                      borderColor={getCurrentColor(
                        colorMode,
                        "blackC.200",
                        "whiteC.200",
                      )}
                      onClick={() => {
                        localStorage.setItem("selectedCaliber", name);
                        setSelectedCaliber(name);
                      }}
                      _hover={{ textDecoration: "none", border: "none" }}
                      wordBreak="normal"
                    >
                      <ArrowForwardIcon mr={{ base: 1, md: 2 }} />
                      {name}
                    </Link>
                  </ListItem>
                );
              })}
            </List>
          </Flex>

          <Link
            as={RouterLink}
            // eslint-disable-next-line
            to={`/${categoryLink.toLowerCase()}`}
            color={"blueC.100"}
            fontWeight="500"
            fontFamily="mainFont"
            fontSize="md"
            mt="auto"
            display={"flex"}
            w={"100%"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Text as="span" display={"inline-block"}>
              Shop {category}
            </Text>{" "}
            <ArrowForwardIcon ml={2} />
          </Link>
        </>
      );
    },
    // eslint-disable-next-line
    [colorMode],
  );

  const InfoBlock = ({ icon: Icon, title, description }) => (
    <Box p={3} borderRadius="lg" display="flex" alignItems="start">
      <Box
        w={{ base: 8, sm: 10 }}
        h={{ base: 8, sm: 10 }}
        bg={getCurrentColor(colorMode, "blueC.800", "grayC.1700")}
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        mr={3}
      >
        <Icon height="20px" width="20px" />
      </Box>
      <Box flex="1" textAlign="left">
        <Text
          fontSize={{ base: "base", sm: "lg", md: "xl" }}
          fontWeight="medium"
          color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
        >
          {title}
        </Text>
        <Text
          fontSize={{ base: "sm", sm: "base" }}
          color={getCurrentColor(colorMode, "grayC.100", "grayC.1600")}
        >
          {description}
        </Text>
      </Box>
    </Box>
  );

  return (
    <Flex
      maxW="1240px"
      w="100%"
      px={{ base: "22px", xl: "10px" }}
      pt={{ base: 8, lg: 6 }}
      mx="auto"
      flexDirection="column"
      mb={{ base: "50px", lg: "60px" }}
    >
      <Heading
        as="h2"
        fontSize={{ base: "3xl", sm: "4xl", md: "5xl", lg: "6xl" }}
        color={getCurrentColor(colorMode, "blackC.200", "whiteC.100")}
        fontFamily="mainFont"
        fontWeight="700"
        mb={{ base: 2, md: 4 }}
        textAlign="center"
      >
        Introducing BulletScout.com
      </Heading>
      <Text
        color={getCurrentColor(colorMode, "grayC.100", "grayC.1600")}
        fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
        fontFamily="mainFont"
        fontWeight="500"
        mb={{ base: 4, md: 6 }}
        textAlign="center"
      >
        Your New Favorite Ammo Search Engine
      </Text>

      <Flex
        direction={{ base: "column", md: "row" }}
        gap={{ base: 4, sm: 6, md: 8, xl: 24 }}
        mb={{ base: 6, md: 4 }}
      >
        <InfoBlock
          icon={DollarSign}
          title="True Price Per Round"
          description="Shipping included from the start."
        />
        <InfoBlock
          icon={LayoutGrid}
          title="Organized by Product"
          description="No more cluttered listings."
        />
        <InfoBlock
          icon={Flag}
          title="Patriot Made"
          description="Built by Americans who shoot straight."
        />
      </Flex>

      <Flex
        flexWrap={{ base: "wrap", xl: "nowrap" }}
        mb={2}
        gap="20px"
      >
        {data.map((item, index) => {
          if (item.category === "Brands") return null;
          return (
            <Flex
              key={index}
              borderRadius="10px"
              w={{ base: "100%", md: "48%", xl: "25%" }}
              minH="350px"
              px="20px"
              py="15px"
              border="1px solid"
              borderColor={getCurrentColor(
                colorMode,
                "grayC.500",
                "transparent",
              )}
              bg={getCurrentColor(colorMode, "blueC.800", "grayC.1700")}
              position="relative"
            >
              <Flex
                flexDirection="column"
                maxW={index % 2 === 0 ? "408px" : "411px"}
                w="100%"
              >
                {renderInfo(item)}
              </Flex>
            </Flex>
          );
        })}
      </Flex>

      <MainPageFreeAmmo />
    </Flex>
  );
};

export default MainPageDiscoverSection;
